import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import mergeWith from 'lodash/mergeWith'

import { GroupRecipientList, GroupRecipientListInit, GroupRecipientListUpdate } from 'common/api/v1/types'
import { AppDispatch, GlobalState, useRoutes } from '../../../store'
import { formTransform, useConfirmationDialog, useUser } from '../../../utils'
import {
  clearGroupList,
  createGroupList,
  getGroupList,
  removeGroupList,
  updateGroupList,
} from '../../../redux/actions/groupListsActions'

import { ButtonsPane, Form, Paper, SafeRouting, TextInput } from '../../common/Form'
import Pendable from '../../common/Pendable'
import Wrapper from '../../common/Wrapper'

import AvailableGroups from './AvailableGroups'
import Groups from './Groups'
import RHF, { FormProps } from '../../common/Form/RHF'

const getInitialState = (
  group: string,
  selectedGroupList?: GroupRecipientList,
): GroupRecipientList & GroupRecipientListUpdate =>
  mergeWith(
    {
      name: '',
      description: '',
      group,
      addGroups: [],
      removeGroups: [],
    },
    selectedGroupList,
    (_initial, existent, key) => {
      if ('description' === key && !existent) {
        return ''
      }
    },
  )

const GroupListForm = ({
  getValues,
  setError,
  setFocus,
  formState,
}: FormProps<GroupRecipientList & GroupRecipientListUpdate>) => {
  const values = getValues()
  const routes = useRoutes()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const showConfirm = useConfirmationDialog()
  const { formErrors, saving } = useSelector(
    ({ groupListsReducer }: GlobalState) => ({
      formErrors: groupListsReducer.formErrors,
      saving: groupListsReducer.saving,
    }),
    shallowEqual,
  )
  useEffect(() => {
    if (Array.isArray(formErrors)) {
      formErrors.forEach((item) => {
        setError(item.name as keyof GroupRecipientList, { type: 'manual', message: item.reason })
      })
      if (formErrors[0]) setFocus(formErrors[0].name as keyof GroupRecipientList)
    }
  }, [formErrors, setError, setFocus])

  const deleteGroupList = () => {
    showConfirm(
      () => void dispatch(removeGroupList(values.id)),
      `Are you sure you want to delete group list ${values.name}?`,
    )
  }

  return (
    <>
      <SafeRouting formState={formState} />
      <Form id="group-list-form" noValidate>
        <Paper title="Metadata">
          <TextInput name="name" label="Name" required autoFocus />
          <TextInput name="description" label="Description" multiline />
        </Paper>
        {!!values.id && (
          <Paper title="Groups" collapsible>
            <Groups />
          </Paper>
        )}
        <Paper title="Select groups">
          <AvailableGroups />
        </Paper>

        <ButtonsPane
          main={{
            Cancel: {
              onClick: () => void navigate(routes.groupLists()),
            },
            Save: { savingState: !!saving, primary: true, type: 'submit' },
          }}
          secondary={values.id ? { 'Remove group list': { onClick: deleteGroupList } } : undefined}
        />
      </Form>
    </>
  )
}

export const Edit = () => {
  const { id } = useParams()
  const dispatch = useDispatch<AppDispatch>()
  useEffect(() => {
    id && dispatch(getGroupList(id))
    return () => {
      dispatch(clearGroupList())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { groupList } = useSelector(
    ({ groupListsReducer }: GlobalState) => ({
      groupList: groupListsReducer.groupList,
    }),
    shallowEqual,
  )
  const { group } = useUser()

  const onSubmit = (draft: Partial<GroupRecipientList & GroupRecipientListUpdate>) => {
    if (groupList)
      dispatch(
        updateGroupList({
          groupList: draft as GroupRecipientList & GroupRecipientListUpdate,
          withRedirection: true,
        }),
      )
    else dispatch(createGroupList(draft as GroupRecipientListInit & Pick<GroupRecipientListUpdate, 'addGroups'>))
  }

  return (
    <Wrapper name={['Groups', 'Lists', id ? groupList?.name : 'New']}>
      <Pendable pending={!!id && !groupList}>
        <RHF
          onSubmit={(values) => {
            onSubmit(formTransform(values))
          }}
          defaultValues={getInitialState(group, groupList)}
          component={GroupListForm}
        />
      </Pendable>
    </Wrapper>
  )
}
